"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Share2, Star, Code as Code2, Search, Globe, FileCheck, ShoppingBag, Target, Cpu, TrendingUp, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
const services = [{
  id: "gbp-optimization",
  title: "Google Business Profile Optimization",
  category: "Local SEO",
  description: "Claim, complete, and continuously refine your GBP profile to lock down top placements in local 3-pack maps and drive direct calls.",
  iconName: "MapPin",
  href: "/LocalSEOPage"
}, {
  id: "citation-building",
  title: "Local Citation Building",
  category: "Local SEO",
  description: "Establish consistent Name, Address, and Phone (NAP) presence across high-authority UK business directories to solidify local trust.",
  iconName: "Share2",
  href: "/LocalSEOPage"
}, {
  id: "review-management",
  title: "Review Management & Reputation",
  category: "Local SEO",
  description: "Implement review acquisition frameworks that encourage genuine customer feedback, boost ratings, and build conversion trust.",
  iconName: "Star",
  href: "/LocalSEOPage"
}, {
  id: "local-schema",
  title: "Local Schema Markup",
  category: "Technical",
  description: "Deploy custom JSON-LD structured data for geo-targeting, organization details, and rich snippets to help engines parse your business accurately.",
  iconName: "Code2",
  href: "/TechnicalSeoPage"
}, {
  id: "national-seo",
  title: "National SEO Strategy",
  category: "Strategy",
  description: "Scale organic reach across the UK with intent-driven content structures, competitive keyword targeting, and sustainable market dominance.",
  iconName: "Search",
  href: "/NationalSeoPage"
}, {
  id: "international-seo",
  title: "International SEO Expansion",
  category: "Global",
  description: "Structure multi-regional and multilingual architectures with correct hreflang tagging and localized strategies for overseas markets.",
  iconName: "Globe",
  href: "/InternationalSeoPage"
}, {
  id: "technical-audits",
  title: "Technical SEO Audits",
  category: "Technical",
  description: "Deep forensic analysis of indexability, crawl budget, Core Web Vitals, and site architecture with clear prioritised fixes.",
  iconName: "FileCheck",
  href: "/SeoAuditsPage"
}, {
  id: "ecommerce-seo",
  title: "Ecommerce SEO",
  category: "Revenue",
  description: "Optimize product taxonomy, category hierarchy, and faceted navigation to turn organic searches into measurable product revenue.",
  iconName: "ShoppingBag",
  href: "/EcommerceSeoPage"
}, {
  id: "b2b-lead-gen",
  title: "B2B SEO & Lead Generation",
  category: "B2B Growth",
  description: "Target decision-makers searching for specialized services with bottom-of-funnel search strategies designed for high-value leads.",
  iconName: "Target",
  href: "/B2BSeoLeadGenerationPage"
}, {
  id: "ai-search-optimization",
  title: "AI Search Optimisation (GEO)",
  category: "Modern SEO",
  description: "Position your brand as a cited entity in Google Gemini, SearchGPT, Perplexity, and AI Overview experiences.",
  iconName: "Cpu",
  href: "/AISearchOptimisationPage"
}, {
  id: "onpage-content",
  title: "On-Page & Content Strategy",
  category: "Content",
  description: "Craft topic clusters and user-first content that answers search intent precisely while satisfying Google's E-E-A-T quality guidelines.",
  iconName: "TrendingUp",
  href: "/ServicesPage"
}, {
  id: "authority-building",
  title: "Authority & Strategic Link Equity",
  category: "Authority",
  description: "Earn relevant, editorial backlinks from reputable niche publications through targeted digital PR and valuable industry assets.",
  iconName: "CheckCircle2",
  href: "/ServicesPage"
}];
const renderIcon = (name: string) => {
  switch (name) {
    case "MapPin":
      return <MapPin className="size-5" />;
    case "Share2":
      return <Share2 className="size-5" />;
    case "Star":
      return <Star className="size-5" />;
    case "Code2":
      return <Code2 className="size-5" />;
    case "Search":
      return <Search className="size-5" />;
    case "Globe":
      return <Globe className="size-5" />;
    case "FileCheck":
      return <FileCheck className="size-5" />;
    case "ShoppingBag":
      return <ShoppingBag className="size-5" />;
    case "Target":
      return <Target className="size-5" />;
    case "Cpu":
      return <Cpu className="size-5" />;
    case "TrendingUp":
      return <TrendingUp className="size-5" />;
    case "CheckCircle2":
      return <CheckCircle2 className="size-5" />;
    default:
      return <Search className="size-5" />;
  }
};
export default function Servicesgrid() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicesgrid" data-nav="light" className="bg-background text-foreground py-20 px-4 sm:px-6 lg:px-8 border-b border-border/50">
      <div className="max-w-7xl mx-auto space-y-12">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold tracking-wider uppercase bg-secondary text-secondary-foreground">
            Direct Consultant Services
          </Badge>
          <h2 className="text-2xl sm:text-3xl font-bold font-default text-foreground tracking-tight">
            Comprehensive SEO Expertise Tailored directly for Your Growth
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg font-default leading-relaxed">
            You work directly with me, Yassine Essaadi. Every strategy, technical fix, and content plan is executed directly to deliver measurable, sustainable search revenue.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => <motion.div key={service.id} data-index={i} variants={cardVariants} className="h-full">
              <Card className="h-full flex flex-col justify-between border-border bg-card text-card-foreground shadow-md transition-[box-shadow,border-color] duration-300 hover:shadow-xl hover:border-primary/40 overflow-hidden rounded-xl">
                <div>
                  <div className="bg-primary text-primary-foreground p-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-primary-foreground/15 text-primary-foreground">
                        {renderIcon(service.iconName)}
                      </div>
                      <span className="font-semibold text-sm tracking-wide text-primary-foreground">
                        {service.title}
                      </span>
                    </div>
                  </div>

                  <CardHeader className="pt-5 pb-2">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Badge variant="outline" className="text-xs font-medium border-border text-muted-foreground">
                        {service.category}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg font-semibold font-default text-foreground leading-snug">
                      {service.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="text-sm text-muted-foreground font-default leading-relaxed pb-4">
                    {service.description}
                  </CardContent>
                </div>

                <CardFooter className="pt-2 pb-5 border-t border-border/40 mt-auto">
                  <Button asChild variant="ghost" size="sm" className="w-full justify-between hover:bg-muted text-primary hover:text-primary font-medium group">
                    <Link to={service.href}>
                      <span>Explore Service</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="bg-muted/50 rounded-2xl p-8 sm:p-10 border border-border text-center max-w-4xl mx-auto space-y-6">
          <h3 className="text-2xl font-bold font-default text-foreground">
            Not sure which SEO service your business needs first?
          </h3>
          <p className="text-muted-foreground text-base max-w-2xl mx-auto font-default">
            Book a free consultation directly with me. I will review your current search visibility and outline a custom roadmap tailored to your market.
          </p>
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/ContactPage">Book a Free SEO Consultation</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/CaseStudiesPage">Explore Case Studies</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}